import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Toaster } from '../components/ui/sonner';
import { FlaskConical, Loader2, RefreshCw, ArrowLeft, HeartPulse } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useDemoConfig } from '../lib/demoConfig';
import { getStaffLandingPath } from '../lib/staffLanding';
import { isDesktop } from '../lib/api';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function TryDemoPage() {
  const demo = useDemoConfig();
  const { demoLogin } = useAuth();
  const navigate = useNavigate();
  const [busy, setBusy] = useState('');

  const startStaff = async (role) => {
    setBusy(role);
    try {
      const user = await demoLogin(role);
      navigate(getStaffLandingPath(user), { replace: true });
    } catch {
      toast.error('Could not open the demo pharmacy. Please try again.');
      setBusy('');
    }
  };

  const startPatient = async () => {
    setBusy('patient');
    try {
      const res = await fetch(`${API_URL}/api/demo/start-patient`, { method: 'POST', credentials: 'include' });
      if (!res.ok) throw new Error();
      const body = await res.json().catch(() => ({}));
      window.location.assign(body.portal_path || '/portal');
    } catch {
      toast.error('Could not open the demo patient app. Please try again.');
      setBusy('');
    }
  };

  const reset = async () => {
    setBusy('reset');
    try {
      const res = await fetch(`${API_URL}/api/demo/reset`, { method: 'POST' });
      if (res.status === 429) toast.message('The demo was just reset — try again in a minute');
      else if (!res.ok) throw new Error();
      else toast.success('Demo pharmacy restored to its starting state');
    } catch {
      toast.error('Could not reset the demo');
    } finally {
      setBusy('');
    }
  };

  return (
    <div className="min-h-screen bg-[#F7F5EF] text-[#12201B]" data-testid="try-demo-page">
      <Toaster position="top-center" richColors />
      <div className="max-w-3xl mx-auto px-6 py-14">
        <Link to="/" className="flex w-fit items-center gap-1.5 text-sm text-[#12201B]/70 hover:text-[#12201B]" data-testid="try-demo-back-link">
          <ArrowLeft className="h-4 w-4" /> Back to DutiLyne
        </Link>
        <div className="mt-10 inline-flex items-center gap-2 rounded-full border border-amber-300 bg-amber-50 px-3 py-1 text-xs font-medium text-amber-800" data-testid="try-demo-sample-badge">
          <FlaskConical className="h-3.5 w-3.5" /> Sample data only — no real pharmacy or patient
        </div>
        <h1 className="mt-5 text-4xl sm:text-5xl font-semibold tracking-tight">Try the demo pharmacy</h1>
        <p className="mt-4 text-base text-[#12201B]/70 max-w-xl">
          Explore {demo.workspace || 'DutiLyne Demo Pharmacy'}, a sealed-off sample pharmacy. Pick a role and you are signed straight in — no password needed.
        </p>

        <div className="mt-10 grid gap-3 sm:grid-cols-3">
          {(demo.roles || []).map((r) => (
            <button key={r.role_key} type="button" onClick={() => startStaff(r.role_key)} disabled={!!busy}
              className="group text-left rounded-xl border border-[#12201B]/15 bg-white p-5 transition-[border-color,transform] duration-200 hover:-translate-y-0.5 hover:border-[#1F7A5A] disabled:opacity-60"
              data-testid={r.testId}>
              <span className="text-sm font-semibold">{r.label}</span>
              <span className="mt-1 block text-xs text-[#12201B]/60">{r.desc}</span>
              <span className="mt-4 inline-flex items-center gap-1 text-xs font-medium text-[#1F7A5A]">
                {busy === r.role_key ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null} Open as {r.label.toLowerCase()}
              </span>
            </button>
          ))}
        </div>

        {demo.patient && !isDesktop() && (
          <button type="button" onClick={startPatient} disabled={!!busy}
            className="mt-3 w-full flex items-center justify-between rounded-xl border border-[#12201B]/15 bg-white p-5 text-left transition-[border-color] duration-200 hover:border-[#1F7A5A] disabled:opacity-60"
            data-testid="try-demo-patient">
            <span>
              <span className="flex items-center gap-2 text-sm font-semibold"><HeartPulse className="h-4 w-4 text-[#1F7A5A]" /> Patient app</span>
              <span className="mt-1 block text-xs text-[#12201B]/60">See prescriptions, refills and chat from a patient's side</span>
            </span>
            {busy === 'patient' && <Loader2 className="h-4 w-4 animate-spin" />}
          </button>
        )}

        <button type="button" onClick={reset} disabled={!!busy}
          className="mt-8 inline-flex items-center gap-1.5 text-xs text-[#12201B]/60 hover:text-[#12201B] disabled:opacity-50"
          data-testid="try-demo-reset-btn">
          <RefreshCw className={`h-3 w-3 ${busy === 'reset' ? 'animate-spin' : ''}`} /> Reset the demo pharmacy to its starting state
        </button>
      </div>
    </div>
  );
}
